import { gql, useQuery } from '@apollo/client';
import IconStarSolid from '@apollo/icons/small/IconStarSolid.svg';
import {
  CardDescription,
  CardHeader,
  CardTitle,
  Link,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@apollo/orbit';
import classnames from 'classnames';
import React, { useMemo, useState } from 'react';

import { EmptyLayout } from 'src/app/account/championDashboardPage/widgets/EmptyLayout';
import * as orgRoutes from 'src/app/account/routes';
import { graphRouteConfig } from 'src/app/graph/routes';
import { Loading } from 'src/components/common/loading/Loading';
import { format } from 'src/lib/format';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';

import {
  ChampionDashboardCard,
  ChampionDashboardCardBody,
} from '../ChampionDashboardCard';

import { packageMetadata } from './UpdatesAvailable/packageMetadata/packageMetadata';
import { UpdatesAvailableCell } from './UpdatesAvailable/UpdatesAvailableCell';
import { UpdatesAvailableModal } from './UpdatesAvailable/UpdatesAvailableModal';

export const NumGraphsWidget = ({ accountId }: { accountId: string }) => {
  const [selectedGraphId, selectGraph] = useState<null | string>(null);
  const { data, loading } = useQuery(
    gql<
      GraphQLTypes.ChampionDashboard_NumGraphsWidget,
      GraphQLTypes.ChampionDashboard_NumGraphsWidgetVariables
    >`
      query ChampionDashboard_NumGraphsWidget($accountId: ID!) {
        account(id: $accountId) {
          id
          name
          graphs {
            id
            title
            variants {
              id
              isFavoriteOfCurrentUser
            }
            statsWindow(from: "-86400") {
              billingUsageStats {
                timestamp
                groupBy {
                  agentVersion
                  variant: schemaTag
                }
              }
            }
          }
        }
      }
    `,
    {
      pollInterval: 60_000,
      variables: { accountId },
    },
  );
  const graphs = data?.account?.graphs ?? [];
  const orgId = data?.account?.id;

  const selectedGraph = useMemo(
    () => data?.account?.graphs.find((graph) => graph.id === selectedGraphId),
    [data, selectedGraphId],
  );

  return (
    <ChampionDashboardCard className="col-span-full lg:col-span-3 xl:col-span-2">
      <CardHeader>
        <CardTitle>Graphs</CardTitle>
        {graphs.length > 0 && (
          <CardDescription>
            There {graphs.length === 1 ? 'is' : 'are'} a total of{' '}
            <Text as="span" variant="bodySb" className="text-secondary">
              {graphs.length} {format.pluralize(graphs.length, 'graph')}
            </Text>{' '}
            in this organization
          </CardDescription>
        )}
      </CardHeader>
      <ChampionDashboardCardBody>
        {loading ? (
          <Loading />
        ) : isMinLengthArray(1, graphs) ? (
          <TableContainer>
            <Table size="condensed">
              <Thead>
                <Tr>
                  <Th className="px-0" />
                  <Th className="pl-1">Graph</Th>
                  <Th>Variants</Th>
                  <Th>Updates</Th>
                </Tr>
              </Thead>
              <Tbody>
                {graphs.map((graph) => (
                  <Tr key={graph.id}>
                    <Td className="w-px pl-2 pr-0 empty:w-0 empty:px-0 empty:before:content-none">
                      <IconStarSolid
                        className={classnames('text-icon-change', {
                          invisible: !graph.variants.some(
                            (variant) => variant.isFavoriteOfCurrentUser,
                          ),
                        })}
                        fill=""
                        stroke=""
                      />
                    </Td>
                    <Td className="w-full max-w-0 pl-1">
                      <Link
                        className="block w-full truncate"
                        to={graphRouteConfig.location({ graphId: graph.id })}
                        title={graph.title}
                      >
                        {graph.title}
                      </Link>
                    </Td>
                    <Td>{graph.variants.length}</Td>
                    <Td>
                      <UpdatesAvailableCell
                        graph={graph}
                        onClick={selectGraph}
                        packageMetadata={packageMetadata}
                      />
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        ) : (
          <EmptyLayout
            title="Create your first graph"
            body={
              <p>
                There are no graphs in this organization yet. Head over to the{' '}
                <Link to={orgRoutes.graphs.location({ orgId: orgId ?? '' })}>
                  Graphs
                </Link>{' '}
                page to get started so you can track usage, changes in your
                schema, and GraphQL performance analytics.
              </p>
            }
          />
        )}
        {selectedGraph && (
          <UpdatesAvailableModal
            orgName={data?.account?.name ?? ''}
            graph={selectedGraph}
            isOpen={!!selectedGraphId}
            onClose={() => selectGraph(null)}
            packageMetadata={packageMetadata}
          />
        )}
      </ChampionDashboardCardBody>
    </ChampionDashboardCard>
  );
};
